import { type FleetAiRecommendationV1, GraphQLRecommendationsRequester } from '@cobliteam/api-lib';
import type { Permission } from '@cobliteam/permissions';
import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import useUser from '../useEntity/useUser';
import useFeatureFlags from '../useFeatureFlags';
import { useDismissAiRecommendation } from './useAiRecommendationsMutation.ts';

export interface AiRecommendationsProgress {
  completedJourneys: number;
  totalJourneys: number;
  score: number;
}

interface AiRecommendationsState {
  recommendations: FleetAiRecommendationV1[];
  progress: AiRecommendationsProgress;
  isLoading: boolean;
  isError: boolean;
  initializedForFleetId: string | null;
  lastFetchedAt: number | null;
  floatingCardDismissed: boolean;
}

const initialState: AiRecommendationsState = {
  recommendations: [],
  progress: { completedJourneys: 0, totalJourneys: 0, score: 0 },
  isLoading: false,
  isError: false,
  initializedForFleetId: null,
  lastFetchedAt: null,
  floatingCardDismissed: typeof window !== 'undefined' && sessionStorage.getItem('ai-rec-floating-dismissed') === 'true',
};

const STALE_TIME_MS = 10 * 60 * 1000; // 10 minutes

export const criticalityConfig: Record<string, { label: string }> = {
  critical: { label: 'Alta prioridade' },
  urgent: { label: 'Média prioridade' },
  important: { label: 'Baixa prioridade' },
  suggestive: { label: 'Baixa prioridade' },
};

export enum AiRecommendationJourney {
  DRIVER_REGISTRATION = 'driver-registration',
  SETUP_DRIVER_ASSOCIATION = 'setup-driver-association',
  USER_PROFILES = 'user-profiles',
  INSTALLATION = 'installation',
  VEHICLE_GROUPS = 'vehicle-groups',
  SETUP_GROUPS = 'setup-groups',
  ALERTS = 'fleet-policies',
  CHECKLISTS = 'checklists',
  GEOFENCES = 'geofences',
  SENSIBILITY_CONFIGURATION = 'sensibility-configuration',
  CAM_AUDIO = 'cam-audio',
  WIFI_CONFIGURATION = 'wifi-configuration',
}

export const permissionsConfig: Record<AiRecommendationJourney, Permission> = {
  'driver-registration': 'create-update-delete:driver',
  'setup-driver-association': 'create-update-delete:driver',
  'user-profiles': 'create-update-delete:users-and-invites',
  installation: 'create-update-delete:crm',
  'vehicle-groups': 'create-update-delete:vehicle',
  'setup-groups': 'create-update-delete:vehicle',
  'fleet-policies': 'config-and-view:custom-alerts',
  checklists: 'full-control:checklist',
  geofences: 'create-update-delete:places-of-interest',
  'sensibility-configuration': 'request:camera-media',
  'cam-audio': 'request:camera-media',
  'wifi-configuration': 'request:camera-media',
};

const recommendationsRequester = new GraphQLRecommendationsRequester();

const useAiRecommendationsStoreBase = create<AiRecommendationsState>(() => ({ ...initialState }));

const dismissFloatingCard = () => {
  useAiRecommendationsStoreBase.setState({ floatingCardDismissed: true });
  try {
    sessionStorage.setItem('ai-rec-floating-dismissed', 'true');
  } catch {
    // sessionStorage unavailable
  }
};

const useAiRecommendationsStore = () => {
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id;
  const { aiRecommendations2 } = useFeatureFlags();
  const { mutate: dismissAiRecommendation } = useDismissAiRecommendation();

  const dismissRecommendation = useCallback(
    (journeyId: string, recommendationType: string) => {
      dismissAiRecommendation(
        { journeyId, recommendationType },
        {
          onSuccess: () => {
            const { recommendations } = useAiRecommendationsStoreBase.getState();
            useAiRecommendationsStoreBase.setState({
              recommendations: recommendations.filter((recommendation) => recommendation.journeyId !== journeyId),
            });
          },
          onError: (error) => {
            console.error('Failed to dismiss AI recommendation', error);
          },
        }
      );
    },
    [dismissAiRecommendation]
  );

  const fetchRecommendations = useCallback(async (targetFleetId: string) => {
    const recs = await recommendationsRequester.generateBasicValueRecommendations(targetFleetId);
    return { recommendations: recs, completedJourneys: 0, totalJourneys: 0, score: 0 };
  }, []);

  const fetch = useCallback(async () => {
    if (!(fleetId && aiRecommendations2)) return;

    const { initializedForFleetId, isLoading, lastFetchedAt } = useAiRecommendationsStoreBase.getState();

    if (initializedForFleetId && initializedForFleetId !== fleetId) {
      useAiRecommendationsStoreBase.setState({ ...initialState });
    } else if (isLoading) {
      return;
    } else if (initializedForFleetId === fleetId && lastFetchedAt && Date.now() - lastFetchedAt < STALE_TIME_MS) {
      return;
    }

    useAiRecommendationsStoreBase.setState({ isLoading: true, isError: false });

    try {
      const data = await fetchRecommendations(fleetId);

      useAiRecommendationsStoreBase.setState({
        recommendations: data.recommendations,
        progress: {
          completedJourneys: data.completedJourneys,
          totalJourneys: data.totalJourneys,
          score: data.score,
        },
        isLoading: false,
        isError: false,
        initializedForFleetId: fleetId,
        lastFetchedAt: Date.now(),
      });
    } catch {
      useAiRecommendationsStoreBase.setState({ isLoading: false, isError: true, initializedForFleetId: fleetId });
    }
  }, [fleetId, aiRecommendations2, fetchRecommendations]);

  const refetch = useCallback(async () => {
    if (!(fleetId && aiRecommendations2)) return;

    const { isLoading } = useAiRecommendationsStoreBase.getState();
    if (isLoading) return;

    useAiRecommendationsStoreBase.setState({ isLoading: true, isError: false });

    try {
      const data = await fetchRecommendations(fleetId);

      useAiRecommendationsStoreBase.setState({
        recommendations: data.recommendations,
        progress: {
          completedJourneys: data.completedJourneys,
          totalJourneys: data.totalJourneys,
          score: data.score,
        },
        isLoading: false,
        isError: false,
        initializedForFleetId: fleetId ?? null,
        lastFetchedAt: Date.now(),
      });
    } catch {
      useAiRecommendationsStoreBase.setState({ isLoading: false, isError: true, initializedForFleetId: fleetId ?? null });
    }
  }, [fleetId, aiRecommendations2, fetchRecommendations]);

  useEffect(() => {
    if (aiRecommendations2) {
      fetch();
    }
  }, [fetch, aiRecommendations2]);

  const { recommendations, progress, isLoading, isError, floatingCardDismissed } = useAiRecommendationsStoreBase();

  return {
    recommendations,
    progress,
    isLoading,
    isError,
    refetch,
    floatingCardDismissed,
    dismissFloatingCard,
    dismissRecommendation,
  };
};

export default useAiRecommendationsStore;
